import React, { ComponentType, useCallback, useMemo, useState } from "react";

import type { Dayjs } from "dayjs";
import { Bar } from "react-chartjs-2";
import {
  BarElement,
  CategoryScale,
  Chart as ChartJS,
  Legend,
  LinearScale,
  Title,
  Tooltip,
  type ChartOptions,
} from "chart.js";

import DatePickerJs from "../../../shared/DatePicker/DatePicker";
import ExportToXlsxButtonJs from "../../../CommonComponents/ExportToXlsxButton/ExportToXlsxButton";
import RebetLoaderJs from "../../../shared/RebetLoader/RebetLoader";
import {
  formatDateNoTimezone,
  getAdminNow,
} from "../../../../utils/dateService";
import {
  formatCompactCurrencyTick,
  formatCurrency,
} from "../../../../utils/financialFormatters";
import { useGetFinancialsDailyPerformance } from "../../../../queries/financials/useGetFinancialsDailyPerformance";
import { DATE_RANGE } from "../../../../utils/Constants";
import {
  defaultDateRangeForViewport,
  defaultStartRangeForViewport,
} from "../../../../utils/narrowViewportChartDates";
import { AppStrings } from "../../../../utils/constants/AppStrings";
import { useCurrencySymbol } from "../../../../hooks/useCurrencySymbol";

import styles from "./DailyPerformance.module.css";

ChartJS.register(CategoryScale, LinearScale, BarElement, Title, Tooltip, Legend);

const CustomDatePicker = DatePickerJs as ComponentType<any>;
const ExportToXlsxButton = ExportToXlsxButtonJs as ComponentType<any>;
const RebetLoader = RebetLoaderJs as ComponentType<any>;

type DateRange = [Dayjs, Dayjs];

const SERIES = [
  { key: "deposits", label: "Deposits", color: "#6177E8" },
  { key: "withdrawals", label: "Withdrawals", color: "#E74C3C" },
  { key: "revenue", label: "Revenue", color: "#2ECC71" },
] as const;

type SeriesKey = (typeof SERIES)[number]["key"];

interface DailyPerformanceProps {
  isApiRunning?: boolean;
}

const DailyPerformance: React.FC<DailyPerformanceProps> = ({
  isApiRunning = false,
}) => {
  const [dates, setDates] = useState<DateRange>(() =>
    defaultDateRangeForViewport(() => {
      const now = getAdminNow();
      return [now.subtract(29, "day").startOf("day"), now.endOf("day")];
    }),
  );
  const [startRange] = useState(() =>
    defaultStartRangeForViewport(DATE_RANGE.LAST_30_DAYS),
  );

  const currencySymbol = useCurrencySymbol() ?? "$";

  const dateFilters = useMemo(
    () => ({
      start_date: dates[0].format("YYYY-MM-DD"),
      end_date: dates[1].format("YYYY-MM-DD"),
    }),
    [dates],
  );

  const { data: points = [], isFetching, isLoading, isError } =
    useGetFinancialsDailyPerformance(dateFilters);

  const getData = useCallback((_queryString: string | null) => {
    // Date changes flow through `dates` -> query key, which refetches.
  }, []);

  const chartData = useMemo(
    () => ({
      labels: points.map((p) => formatDateNoTimezone(p.date, "MMM D")),
      datasets: SERIES.map((s) => ({
        label: s.label,
        data: points.map((p) => p[s.key as SeriesKey]),
        backgroundColor: s.color,
        borderRadius: 3,
        barPercentage: 1.0,
        categoryPercentage: 0.7,
      })),
    }),
    [points],
  );

  const chartOptions = useMemo<ChartOptions<"bar">>(
    () => ({
      responsive: true,
      maintainAspectRatio: false,
      datasets: {
        bar: {
          barPercentage: 1.0,
          categoryPercentage: 0.7,
        },
      },
      interaction: { mode: "index" as const, intersect: false },
      scales: {
        x: {
          grid: { display: false },
          ticks: {
            color: "#9aa3b1",
            font: { size: 11 },
            maxRotation: 45,
            minRotation: 45,
          },
        },
        y: {
          beginAtZero: true,
          grid: { color: "rgba(255,255,255,0.06)" },
          ticks: {
            color: "#9aa3b1",
            font: { size: 11 },
            callback: (value: number | string) =>
              formatCompactCurrencyTick(Number(value), currencySymbol),
          },
        },
      },
      plugins: {
        legend: { display: false },
        title: { display: false },
        datalabels: { display: false },
        tooltip: {
          enabled: true,
          mode: "index" as const,
          intersect: false,
          backgroundColor: "#252729",
          padding: 12,
          displayColors: true,
          titleFont: { family: "Poppins-regular", size: 13, weight: 600 },
          bodyFont: { family: "Poppins-regular", size: 13 },
          callbacks: {
            title: (items: { dataIndex: number }[]) => {
              if (!items?.length) return "";
              const iso = points[items[0].dataIndex]?.date;
              return iso ? formatDateNoTimezone(iso, "MMM D, YYYY") : "";
            },
            // Append the series label after the value so admins can tell
            // which figure is which when the tooltip lists all three. The
            // dataset index maps 1:1 to `SERIES`, so we look up the label
            // from there rather than relying on `dataset.label` (which is
            // undefined for these datasets by design).
            label: (item: { raw: unknown; datasetIndex: number }) => {
              const series = SERIES[item.datasetIndex];
              const value = formatCurrency(Number(item.raw), currencySymbol);
              return series?.label ? `${value} ${series.label}` : value;
            },
          },
        },
      },
    }),
    [points, currencySymbol],
  );

  const exportRows = useMemo(
    () =>
      points.map((p) => ({
        date: p.date,
        deposits: p.deposits,
        withdrawals: p.withdrawals,
        revenue: p.revenue,
      })),
    [points],
  );

  const showLoading = isApiRunning || isLoading || isFetching;

  return (
    <section className={styles.card} aria-label="Daily Performance">
      <header className={styles.header}>
        <div className={styles.titleGroup}>
          <h2 className={styles.title}>Daily Performance</h2>
          <ul className={styles.legend}>
            {SERIES.map((s) => (
              <li key={s.key} className={styles.legendItem}>
                <span
                  className={styles.legendDot}
                  style={{ backgroundColor: s.color }}
                  aria-hidden="true"
                />
                {s.label}
              </li>
            ))}
          </ul>
        </div>
        <div className={styles.controls}>
          <CustomDatePicker
            getData={getData}
            dates={dates}
            setDates={setDates}
            showPresets={true}
            startRange={startRange}
          />
          <ExportToXlsxButton
            data={exportRows}
            filename="daily-performance.xlsx"
          />
        </div>
      </header>

      <div className={styles.chartContainer}>
        {showLoading ? (
          <div className={styles.loaderOverlay} data-testid="chart-loader">
            <RebetLoader width={100} height={100} />
          </div>
        ) : isError ? (
          <p
            className={styles.errorBanner}
            role="alert"
            data-testid="daily-performance-error"
          >
            {AppStrings.FINANCIALS_DAILY_LOAD_ERROR}
          </p>
        ) : points.length === 0 ? (
          <p className={styles.emptyMessage} data-testid="daily-performance-empty">
            {AppStrings.FINANCIALS_DAILY_EMPTY_STATE}
          </p>
        ) : (
          <Bar data={chartData} options={chartOptions} />
        )}
      </div>
    </section>
  );
};

export default DailyPerformance;
